import { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import apiClient from '@/lib/api/client';

// ── Blog docket (CR #171) ───────────────────────────────────────────────────
// Erik gives the AI writer the facts (and optionally a job + suggested photos
// from that job's shared documents); the writer drafts a post in the public
// site's "Picture this…" voice. Drafts queue here for approval — nothing is
// public until Approve, and the public API enforces published-only anyway.

interface JobLite { id: number; title: string; jobNumber: string; status: string }
interface CtxPhoto { id: number; file_name: string; file_path: string; caption?: string | null }
interface JobCtx {
  job: { id: number; title: string; status: string; description?: string | null };
  logs: { log_date: string; work_performed: string }[];
  tasks: { title: string; completed_at: string }[];
  photos: CtxPhoto[];
}
interface Post {
  id: number; job_id: number | null; job_title?: string | null;
  title: string; slug: string; body: string; excerpt: string | null;
  hero_photo: string | null; photos: string | null;
  status: 'draft' | 'approved' | 'published' | 'rejected';
  ai_generated: number; erik_notes: string | null;
  created_at: string; published_at: string | null;
}

const photoUrl = (p: string) => `/${p.replace(/^\/+/, '')}`;
const parsePhotos = (raw: string | null): string[] => {
  if (!raw) return [];
  try { const a = JSON.parse(raw); return Array.isArray(a) ? a : []; } catch { return []; }
};

const STATUS_STYLE: Record<Post['status'], string> = {
  draft: 'bg-amber-100 text-amber-800',
  approved: 'bg-sky-100 text-sky-700',
  published: 'bg-green-100 text-green-700',
  rejected: 'bg-gray-200 text-gray-500',
};

export const BlogDocketPage = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState<JobLite[]>([]);
  // Composer state
  const [jobId, setJobId] = useState('');
  const [notes, setNotes] = useState('');
  const [ctx, setCtx] = useState<JobCtx | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [hero, setHero] = useState('');
  const [writing, setWriting] = useState(false);
  const [error, setError] = useState('');
  // Docket state
  const [open, setOpen] = useState<number | null>(null);
  const [filter, setFilter] = useState<'all' | Post['status']>('all');
  const [editing, setEditing] = useState<number | null>(null);
  const [eTitle, setETitle] = useState(''); const [eExcerpt, setEExcerpt] = useState(''); const [eBody, setEBody] = useState('');
  const [busy, setBusy] = useState<number | null>(null);

  const load = () =>
    apiClient.get('/blog').then(r => { setPosts(r.data.data ?? []); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => {
    load();
    apiClient.get('/jobs', { params: { limit: 100 } })
      .then(r => setJobs(r.data.data?.jobs ?? [])).catch(() => {});
  }, []);

  useEffect(() => {
    setCtx(null); setPicked([]); setHero('');
    if (!jobId) return;
    apiClient.get(`/blog/job-context/${jobId}`).then(r => setCtx(r.data.data)).catch(() => {});
  }, [jobId]);

  const togglePhoto = (p: string) => {
    setPicked(prev => {
      const next = prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p];
      if (!next.includes(hero)) setHero(next[0] || '');
      if (!prev.includes(p) && !hero) setHero(p);
      return next;
    });
  };

  const generate = async () => {
    if (!notes.trim() || writing) return;
    setWriting(true); setError('');
    try {
      await apiClient.post('/blog/generate', {
        job_id: jobId ? Number(jobId) : null,
        erik_notes: notes.trim(),
        photos: picked,
        hero_photo: hero || picked[0] || null,
      }, { timeout: 180000 });
      setNotes(''); setPicked([]); setHero('');
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'The writer failed — try again.');
    } finally { setWriting(false); }
  };

  const act = async (id: number, action: 'approve' | 'reject' | 'regenerate' | 'delete') => {
    if (action === 'delete' && !window.confirm('Delete this post for good?')) return;
    setBusy(id);
    try {
      if (action === 'delete') await apiClient.delete(`/blog/${id}`);
      else await apiClient.post(`/blog/${id}/${action}`, {}, { timeout: 180000 });
      await load();
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || `Could not ${action} the post.`);
    } finally { setBusy(null); }
  };

  const startEdit = (p: Post) => { setEditing(p.id); setETitle(p.title); setEExcerpt(p.excerpt || ''); setEBody(p.body || ''); };
  const saveEdit = async (id: number) => {
    setBusy(id);
    try {
      await apiClient.put(`/blog/${id}`, { title: eTitle, excerpt: eExcerpt, body: eBody });
      setEditing(null); await load();
    } catch { setError('Save failed.'); } finally { setBusy(null); }
  };

  const shown = posts.filter(p => filter === 'all' || p.status === filter);
  const pending = posts.filter(p => p.status === 'draft').length;

  return (
    <MainLayout>
      <LargeTitleHeader
        title="Blog"
        subtitle="Give the writer the facts, it drafts a post in the site's 'Picture this…' voice — nothing goes live until you approve it"
      />

      {/* Composer */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Compose a draft</p>
        <div className="flex gap-2 flex-wrap">
          <select value={jobId} onChange={e => setJobId(e.target.value)}
            className="border border-gray-300 rounded-lg px-2 h-10 bg-white text-sm text-gray-700 min-w-[16rem]">
            <option value="">No job — general post</option>
            {jobs.map(j => <option key={j.id} value={j.id}>{j.jobNumber} — {j.title} ({j.status})</option>)}
          </select>
        </div>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
          placeholder="What should this post cover? Give the writer the facts — what the crew built, what went well, what you're proud of. It will only use what you write here plus the job's client-shared progress notes."
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#C77D3F]/40" />

        {ctx && (
          <div className="space-y-2 text-xs">
            {(ctx.logs.length > 0 || ctx.tasks.length > 0) && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-bold text-gray-500 uppercase tracking-wide text-[10px] mb-1">What the writer will also see (client-shared only)</p>
                {ctx.logs.slice(0, 4).map((l, i) => (
                  <p key={i} className="text-gray-600 truncate">Log {String(l.log_date).slice(0, 10)}: {l.work_performed}</p>
                ))}
                {ctx.tasks.slice(0, 5).map((t, i) => <p key={`t${i}`} className="text-gray-600 truncate">Done: {t.title}</p>)}
              </div>
            )}
            <div>
              <p className="font-bold text-gray-500 uppercase tracking-wide text-[10px] mb-1.5">
                Suggested photos {ctx.photos.length === 0 && <span className="normal-case font-normal text-gray-400">— none on this job yet (upload photos to the job's documents first)</span>}
              </p>
              {ctx.photos.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {ctx.photos.map(ph => {
                    const sel = picked.includes(ph.file_path);
                    return (
                      <button key={ph.id} onClick={() => togglePhoto(ph.file_path)} title={ph.caption || ph.file_name}
                        className={`relative w-24 h-24 rounded-lg overflow-hidden border-2 ${sel ? 'border-[#C77D3F]' : 'border-transparent hover:border-gray-300'}`}>
                        <img src={photoUrl(ph.file_path)} alt={ph.file_name} className="w-full h-full object-cover" loading="lazy" />
                        {sel && (
                          <span className="absolute top-1 right-1 material-symbols-outlined text-[#C77D3F] bg-white rounded-full text-base">check_circle</span>
                        )}
                        {sel && hero === ph.file_path && (
                          <span className="absolute bottom-0 inset-x-0 bg-[#C77D3F] text-white text-[9px] font-bold text-center">HERO</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              {picked.length > 1 && (
                <p className="text-gray-400 mt-1">Hero photo: <select value={hero} onChange={e => setHero(e.target.value)} className="border border-gray-200 rounded px-1 py-0.5">
                  {picked.map(p => <option key={p} value={p}>{p.split('/').pop()}</option>)}
                </select></p>
              )}
            </div>
          </div>
        )}

        {error && <p className="text-xs text-red-600">{error}</p>}
        <button onClick={generate} disabled={writing || !notes.trim()}
          className="h-10 px-4 rounded-lg bg-[#C77D3F] text-white text-sm font-bold disabled:opacity-40 flex items-center gap-1.5">
          <span className="material-symbols-outlined text-base">{writing ? 'hourglass_top' : 'auto_awesome'}</span>
          {writing ? 'Writing… this can take a minute' : 'Write it'}
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {(['all', 'draft', 'published', 'rejected'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`h-8 px-3 rounded-full text-xs font-bold border ${filter === f ? 'bg-[#C77D3F] text-white border-[#C77D3F]' : 'bg-white text-gray-600 border-gray-200'}`}>
            {f === 'all' ? `All (${posts.length})` : f[0].toUpperCase() + f.slice(1)}
          </button>
        ))}
        {pending > 0 && <span className="ml-auto text-xs text-amber-700 font-semibold">{pending} draft{pending > 1 ? 's' : ''} awaiting your approval</span>}
      </div>

      {/* Docket */}
      {loading ? (
        <div className="h-24 animate-pulse rounded-xl bg-gray-100" />
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <span className="material-symbols-outlined text-gray-300 text-5xl block mb-2">rss_feed</span>
          <p className="text-sm text-gray-500">No posts here yet — write the facts above and let the writer draft one.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {shown.map(p => {
            const photos = parsePhotos(p.photos);
            return (
              <div key={p.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <button onClick={() => setOpen(o => o === p.id ? null : p.id)} className="w-full flex items-center gap-2.5 px-4 py-3 text-left">
                  {p.hero_photo
                    ? <img src={photoUrl(p.hero_photo)} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                    : <span className="material-symbols-outlined text-xl text-gray-300">article</span>}
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-gray-900 truncate">{p.title}</span>
                    <span className="block text-[11px] text-gray-400 truncate">
                      {new Date(p.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
                      {p.job_title ? ` · ${p.job_title}` : ''}
                      {p.ai_generated ? ' · AI draft' : ''}
                      {p.status === 'published' && p.published_at ? ` · live since ${new Date(p.published_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}` : ''}
                    </span>
                  </span>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                </button>
                {open === p.id && (
                  <div className="border-t border-gray-100 px-4 py-3 space-y-3">
                    {editing === p.id ? (
                      <div className="space-y-2">
                        <input value={eTitle} onChange={e => setETitle(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold" placeholder="Title" />
                        <input value={eExcerpt} onChange={e => setEExcerpt(e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Excerpt (one sentence for the card)" />
                        <textarea value={eBody} onChange={e => setEBody(e.target.value)} rows={10}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-xs font-mono resize-y" placeholder="Body (HTML <p> paragraphs)" />
                        <div className="flex gap-2">
                          <button onClick={() => saveEdit(p.id)} disabled={busy === p.id || !eTitle.trim()}
                            className="h-8 px-3 rounded-lg bg-[#C77D3F] text-white text-xs font-bold disabled:opacity-40">Save</button>
                          <button onClick={() => setEditing(null)} className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {p.excerpt && <p className="text-xs text-gray-500 italic">{p.excerpt}</p>}
                        <div className="prose prose-sm max-w-none text-sm text-gray-700 [&_p]:mb-2 [&_h3]:font-bold [&_h3]:text-gray-900"
                          dangerouslySetInnerHTML={{ __html: p.body || '' }} />
                        {photos.length > 0 && (
                          <div className="flex gap-2 flex-wrap">
                            {photos.map(ph => (
                              <img key={ph} src={photoUrl(ph)} alt=""
                                className={`w-20 h-20 rounded-lg object-cover ${ph === p.hero_photo ? 'ring-2 ring-[#C77D3F]' : ''}`} />
                            ))}
                          </div>
                        )}
                        {p.erik_notes && (
                          <p className="text-[11px] text-gray-400 bg-gray-50 rounded-lg p-2">
                            <span className="font-bold uppercase tracking-wide text-[9px] block">Your notes to the writer</span>
                            {p.erik_notes}
                          </p>
                        )}
                        <div className="flex gap-2 flex-wrap pt-1">
                          {p.status !== 'published' && (
                            <button onClick={() => act(p.id, 'approve')} disabled={busy === p.id}
                              className="h-8 px-3 rounded-lg bg-green-600 text-white text-xs font-bold disabled:opacity-40 flex items-center gap-1">
                              <span className="material-symbols-outlined text-sm">publish</span>Approve &amp; publish
                            </button>
                          )}
                          {p.status === 'published' && (
                            <button onClick={() => act(p.id, 'reject')} disabled={busy === p.id}
                              className="h-8 px-3 rounded-lg border border-amber-300 text-amber-700 text-xs font-bold disabled:opacity-40">Unpublish</button>
                          )}
                          {p.status === 'draft' && (
                            <button onClick={() => act(p.id, 'reject')} disabled={busy === p.id}
                              className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold disabled:opacity-40">Reject</button>
                          )}
                          <button onClick={() => startEdit(p)} className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold">Edit</button>
                          {p.erik_notes && p.status !== 'published' && (
                            <button onClick={() => act(p.id, 'regenerate')} disabled={busy === p.id}
                              className="h-8 px-3 rounded-lg border border-gray-200 text-gray-600 text-xs font-bold disabled:opacity-40 flex items-center gap-1">
                              <span className="material-symbols-outlined text-sm">refresh</span>
                              {busy === p.id ? 'Rewriting…' : 'Regenerate'}
                            </button>
                          )}
                          <button onClick={() => act(p.id, 'delete')} disabled={busy === p.id}
                            className="h-8 px-3 rounded-lg border border-red-200 text-red-600 text-xs font-bold ml-auto disabled:opacity-40">Delete</button>
                        </div>
                        {p.status === 'published' && (
                          <p className="text-[11px] text-gray-400">
                            Live at <a className="text-[#C77D3F] underline" href={`https://ochrepoint.ca/blog/?post=${p.slug}`} target="_blank" rel="noreferrer">ochrepoint.ca/blog/?post={p.slug}</a>
                          </p>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </MainLayout>
  );
};
